/**
 * Last-resort guard around a ticket screen.
 *
 * TanStack Query already surfaces *request* failures as `error` on each hook,
 * and every screen renders `<ErrorState>` for those. This boundary catches the
 * other kind: a render-time crash from a payload the contract did not predict —
 * a null where a model promised an object, an enum member added by an Ivanti
 * administrator that reaches a lookup the wrong way. Users are non-technical
 * employees; a blank white page is the worst possible answer.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

import { ErrorState } from "@/components/common";
import { reportClientError } from "@/lib/api/client-error-report";
import { logger } from "@/lib/logger";

export interface TicketErrorBoundaryProps {
  children: ReactNode;
  /** Rendered instead of the default error state. */
  fallback?: ReactNode;
}

interface TicketErrorBoundaryState {
  error: Error | null;
}

export class TicketErrorBoundary extends Component<
  TicketErrorBoundaryProps,
  TicketErrorBoundaryState
> {
  public state: TicketErrorBoundaryState = { error: null };

  public static getDerivedStateFromError(error: Error): TicketErrorBoundaryState {
    return { error };
  }

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error("[tickets] render failed", error, info.componentStack);
    reportClientError({ kind: "RENDER", message: `${error.name}: ${error.message}` });
  }

  private readonly handleRetry = (): void => {
    this.setState({ error: null });
  };

  public render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error === null) {
      return children;
    }
    if (fallback !== undefined) {
      return fallback;
    }
    return <ErrorState error={error} onRetry={this.handleRetry} />;
  }
}
